import { interval, Observable } from 'rxjs'
import { distinctUntilChanged, mergeMap, map } from 'rxjs/operators'

import makePromise from './make-promise'
import OutlookRest from './rest'
import { getOffice } from './office'
import { isDesktopMac, isDesktopWindows } from './client'
import { addBreadcrumb, captureException } from '../sentry-config'
import { Recipient, Recipients } from '../types/loft'
import { InterlocutorDetails } from '../types/person'

// The underlying Office mailbox item exposes a varying API surface depending on
// host (compose/read, message/appointment); this class is a unified facade over
// all of them, so the raw `item` is treated opaquely.
type OfficeItem = any
type CustomPropertyValues = Record<string, unknown>

type RestMessageCache = {
  subject: string
  body: string
  attachments: unknown[]
} | null

type MessageDetails = {
  emailAddress: string | null
  internetMessageId: string
}

export const saveItem = async (office: typeof Office = getOffice()): Promise<unknown> => {
  try {
    return await makePromise(office.context.mailbox.item?.saveAsync, office.context.mailbox.item)()
  } catch (error) {
    console.error('saveItem error: ', error)
  }

  return
}

export const getMessageIds = async (
  office: typeof Office = getOffice()
): Promise<{ ewsItemId: string; restItemId: string }> => {
  const ewsItemId = (await saveItem(office)) as string

  const checkId = office.context.mailbox.convertToRestId(
    ewsItemId,
    office.MailboxEnums.RestVersion.v2_0
  )

  addBreadcrumb({
    message: 'Item: getMessageIds',
    category: 'office-js',
    data: { ewsItemId, itemId: office.context.mailbox.item?.itemId, checkId },
  })

  return {
    ewsItemId,
    restItemId: office.context.mailbox.convertToRestId(
      ewsItemId,
      office.MailboxEnums.RestVersion.v2_0
    ),
  }
}

const transformRecipient = ({
  displayName,
  emailAddress,
  recipientType,
}: Recipient): Recipient => ({
  displayName,
  emailAddress,
  recipientType,
})

export default class OutlookItem {
  office: typeof Office
  rest: OutlookRest

  interlocutorObservable: Observable<MessageDetails>
  attendeesObservable: Observable<Recipient[]>
  recurrenceObservable: Observable<boolean>

  restMessage: RestMessageCache = null

  constructor(office: typeof Office) {
    this.office = office
    this.rest = new OutlookRest(office as any)

    this.interlocutorObservable = interval(1000).pipe(
      mergeMap(() => this.getMessageDetails()),
      distinctUntilChanged(
        (a, b) => a.emailAddress === b.emailAddress && a.internetMessageId === b.internetMessageId
      )
    )

    this.attendeesObservable = interval(1000).pipe(
      mergeMap(() => this.getAttendees()),
      distinctUntilChanged((a, b) => a.length === b.length)
    )

    this.recurrenceObservable = interval(1000).pipe(
      mergeMap(() => this.getRecurrence()),
      map(recurrence => recurrence !== null),
      distinctUntilChanged()
    )
  }

  get item(): OfficeItem {
    return this.office.context.mailbox.item
  }

  hasItem(): boolean {
    return Boolean(this.item)
  }

  hasItemId(): boolean {
    return this.hasItem() && Boolean(this.item.itemId)
  }

  save(): Promise<unknown> {
    return makePromise(this.item.saveAsync, this.item)()
  }

  async close(): Promise<unknown> {
    return this.item.close()
  }

  async getMessageDetails(): Promise<MessageDetails> {
    const { emailAddress } = await this.getInterlocutorDetails()

    return { emailAddress, internetMessageId: this.getInternetMessageId() }
  }

  async getAttendees(): Promise<Recipient[]> {
    if (!this.item || !this.item.requiredAttendees || !this.item.optionalAttendees) {
      return []
    }

    const requiredAttendees = await makePromise(this.item.requiredAttendees?.getAsync, this.item)()
    const optionalAttendees = await makePromise(this.item.optionalAttendees?.getAsync, this.item)()

    return [
      ...(requiredAttendees as Recipient[]),
      ...(optionalAttendees as Recipient[]).map(attendee => ({
        ...attendee,
        optional: true,
      })),
    ]
  }

  async getOrganizer(): Promise<unknown> {
    const organizer = await makePromise(this.item.organizer.getAsync, this.item)()

    return organizer
  }

  async getItemType(): Promise<string> {
    return this.item.itemType
  }

  getInternetMessageId(): string {
    if (this.item && this.item.internetMessageId) {
      return this.item.internetMessageId.replace(/^.|.$/g, '')
    }
    return ''
  }

  async getRecipients(): Promise<Recipients> {
    const [to, cc, bcc] = await Promise.all([this.getTo(), this.getCc(), this.getBcc()])

    return {
      to: to.map(transformRecipient),
      cc: cc.map(transformRecipient),
      bcc: bcc.map(transformRecipient),
    }
  }

  getCustomProperties(): Promise<Office.CustomProperties> {
    return makePromise(this.item.loadCustomPropertiesAsync, this.item)() as Promise<
      Office.CustomProperties
    >
  }

  async getCustomPropertyValues(
    defaultValues: CustomPropertyValues = {}
  ): Promise<CustomPropertyValues> {
    const keys = Object.keys(defaultValues)

    if (!keys.length) {
      return {}
    }

    try {
      const customProperties = await this.getCustomProperties()

      return keys.reduce<CustomPropertyValues>((acc, key) => {
        const value = customProperties.get(key)
        acc[key] = value === undefined ? defaultValues[key] : value
        return acc
      }, {})
    } catch (e) {
      return { ...defaultValues }
    }
  }

  async getCustomPropertyValue(key: string, defaultValue: unknown = null): Promise<unknown> {
    const values = await this.getCustomPropertyValues({ [key]: defaultValue })
    return values[key]
  }

  getBody = async (
    coercionType: 'html' | 'text' = 'html',
    options: Record<string, unknown> = {}
  ): Promise<string> => {
    if (this.restMessage) {
      return this.restMessage.body
    }

    try {
      let bodyText = (await makePromise(this.item.body.getAsync, this.item.body)(
        coercionType,
        options
      )) as string

      // in bodyText replace img src (current with token and attachment_id) with originalsrc
      if (coercionType === 'html' && bodyText) {
        // Use DOMParser to safely parse HTML without executing scripts
        const parser = new DOMParser()
        const doc = parser.parseFromString(bodyText, 'text/html')

        // Find all images and update src attributes safely
        const images = doc.getElementsByTagName('img')
        for (let i = 0; i < images.length; i++) {
          const img = images[i]
          const originalSrc = img.getAttribute('originalsrc')
          const imgSrc = img.getAttribute('src')

          if (originalSrc) {
            img.setAttribute('src', originalSrc)
          } else if (
            !originalSrc &&
            imgSrc &&
            imgSrc.startsWith('cid:') &&
            imgSrc.indexOf('@') > -1
          ) {
            img.setAttribute('src', imgSrc.substring(0, imgSrc.indexOf('@')))
          }
        }

        // Extract content safely without using innerHTML
        // Serialize the modified DOM back to string using XMLSerializer
        const serializer = new XMLSerializer()

        // extracting styles from head and adding to body to preserve styling
        const headStyles = doc.head
          ? Array.from(doc.head.querySelectorAll('style'))
              .map(style => `<style>${style.textContent}</style>`)
              .join('')
          : ''

        if (doc.body) {
          bodyText = serializer.serializeToString(doc.body)
          // Remove the outer <body> tags to get just the inner content
          bodyText = bodyText.replace(/^<body[^>]*>/, '').replace(/<\/body>$/, '')
        } else {
          // Fallback: serialize the entire document and extract body content
          const fullDoc = serializer.serializeToString(doc)
          const bodyMatch = fullDoc.match(/<body[^>]*>(.*?)<\/body>/s)
          bodyText = bodyMatch ? bodyMatch[1] : bodyText
        }
        if (headStyles) {
          bodyText = headStyles + bodyText
        }
      }

      return bodyText
    } catch (error) {
      addBreadcrumb({
        message: 'Error getting body: failed during replacing image src',
        category: 'office-js',
        data: { error: error.message },
      })
      return makePromise(this.item.body.getAsync, this.item.body)(coercionType, options) as Promise<
        string
      >
    }
  }

  getBodyType(): Promise<string> {
    return makePromise(this.item.body.getTypeAsync, this.item.body)() as Promise<string>
  }

  async setBody(body: string | undefined): Promise<unknown> {
    const setBody = makePromise(this.item.body.setAsync, this.item.body)

    const coercionType = await this.getBodyType()
    return setBody(body || '', { coercionType })
  }

  async prependBody(value: string): Promise<unknown> {
    const prependBody = makePromise(this.item.body.prependAsync, this.item.body)

    const coercionType = await this.getBodyType()
    return prependBody(value, { coercionType })
  }

  async insertToBody(snippet: string): Promise<unknown> {
    const setBody = makePromise(this.item.body.setSelectedDataAsync, this.item.body)

    const coercionType = await this.getBodyType()
    return setBody(snippet, { coercionType })
  }

  async getSubject(): Promise<string> {
    if (this.item.normalizedSubject) {
      return this.item.normalizedSubject
    }
    if (this.restMessage) {
      return this.restMessage.subject
    }
    return makePromise(this.item.subject.getAsync, this.item.subject)() as Promise<string>
  }

  async getRecurrence(): Promise<unknown | null> {
    if (!this.item || !this.item.recurrence) {
      return null
    }

    const recurrence = await makePromise(this.item.recurrence?.getAsync, this.item)()

    return recurrence
  }

  async getItemId(): Promise<string | null> {
    try {
      const itemId = (await makePromise(this.item.getItemIdAsync, this.item)()) as string

      return itemId
    } catch (e) {
      if (e.name !== 'ItemNotSaved') {
        captureException(e)
      }
      return null
    }
  }

  async getLocation(): Promise<string> {
    const location = await makePromise(this.item.location.getAsync, this.item)()

    return location as string
  }

  async getEndTime(): Promise<string> {
    const end = await makePromise(this.item.end.getAsync, this.item)()

    return end as string
  }

  async getStartTime(): Promise<string> {
    const start = await makePromise(this.item.start.getAsync, this.item)()

    return start as string
  }

  getFrom(): { emailAddress: string; displayName?: string } {
    if (this.item && this.item.from && this.item.from.emailAddress) {
      return this.item.from
    }
    return this.office.context.mailbox?.userProfile
  }

  async getRecipientsByType(type: 'to' | 'cc' | 'bcc'): Promise<Recipient[]> {
    const recipients = this.item[type]

    if (Array.isArray(recipients)) {
      return recipients
    }

    if (recipients) {
      return makePromise(recipients.getAsync, recipients)() as Promise<Recipient[]>
    }

    return []
  }

  getTo(): Promise<Recipient[]> {
    return this.getRecipientsByType('to')
  }

  getCc(): Promise<Recipient[]> {
    return this.getRecipientsByType('cc')
  }

  getBcc(): Promise<Recipient[]> {
    return this.getRecipientsByType('bcc')
  }

  attachmentPayload(attachment: any, content: string | null): Record<string, unknown> {
    if (attachment.isInline === true && !attachment.contentId) {
      attachment.contentId = attachment.name
    }

    // On classic Outlook for Windows, body.getAsync() returns CID references without the
    // MIME domain suffix (e.g. "cid:image001.jpg" rather than "cid:image001.jpg@01D12345.67890000")
    // because getBody() strips everything from '@' onward.  The attachment's contentId still
    // carries the full MIME form, so we normalise it here to keep both sides consistent.
    //
    // This guard is intentional: OWA (even with REST disabled) uses the originalsrc attribute
    // and getBody() leaves that CID intact (including '@domain'), so we must NOT strip there.
    let contentId = attachment.contentId || null
    if (contentId && contentId.indexOf('@') > -1 && isDesktopWindows(this.office)) {
      contentId = contentId.substring(0, contentId.indexOf('@'))
    }

    return {
      ...attachment,
      Id: attachment.id,
      Name: attachment.name,
      ContentId: contentId,
      ContentType: attachment.contentType || null,
      IsInline: attachment.isInline || null,
      ContentBytes: content || null,
    }
  }

  private async getAttachmentsList(): Promise<Office.AttachmentDetailsCompose[]> {
    return await makePromise(
      Office.context.mailbox?.item?.getAttachmentsAsync,
      Office.context.mailbox?.item
    )()
  }

  async getAttachments(): Promise<Record<string, unknown>[]> {
    if (this.restMessage) {
      return (this.restMessage.attachments as Record<string, unknown>[]) || []
    }

    try {
      // Get attachments list
      let getAttachmentsResult = await this.getAttachmentsList()

      // On classic Outlook for Windows a freshly pasted (clipboard) inline image is
      // not committed as a retrievable MIME attachment until the draft is saved. If
      // we enumerate attachments before that happens, getAttachmentsAsync returns an
      // empty list while the body still carries the image's `cid:` reference — which
      // then ships as a broken/linked image once lofted (see SL-114476). Saving the
      // item and retrying a bounded number of times lets Outlook materialise the
      // inline attachment so we can send its bytes with the message.
      if (
        (!getAttachmentsResult || getAttachmentsResult.length === 0) &&
        isDesktopWindows(this.office)
      ) {
        let lastSaveError: unknown
        for (let attempt = 0; attempt < 2; attempt++) {
          await this.save().catch(err => {
            lastSaveError = err
            addBreadcrumb({
              message: 'getAttachments: save failed during inline-image retry',
              category: 'office-js',
              data: { attempt, error: err instanceof Error ? err.message : String(err) },
            })
          })
          await new Promise(resolve => setTimeout(resolve, 300))
          getAttachmentsResult = await this.getAttachmentsList()
          if (getAttachmentsResult && getAttachmentsResult.length > 0) {
            break
          }
        }

        addBreadcrumb({
          message: 'getAttachments: retried after save on classic Windows',
          category: 'office-js',
          data: {
            recovered: (getAttachmentsResult?.length ?? 0) > 0,
            saveFailed: lastSaveError !== undefined,
          },
        })
      }

      if (!getAttachmentsResult || getAttachmentsResult.length === 0) {
        return []
      }

      // Get content for each attachment
      const attachmentsWithContent = await Promise.all(
        getAttachmentsResult.map(async attachment => {
          try {
            const getContentResult = await makePromise(
              Office.context.mailbox?.item?.getAttachmentContentAsync,
              Office.context.mailbox?.item
            )(attachment.id)

            return this.attachmentPayload(attachment, getContentResult?.content ?? null)
          } catch (error) {
            addBreadcrumb({
              message: 'Failed to get attachment content',
              category: 'office-js',
              data: { error: error.message },
            })
            return this.attachmentPayload(attachment, null)
          }
        })
      )

      return attachmentsWithContent
    } catch (error) {
      addBreadcrumb({
        message: 'Failed to get attachments',
        category: 'office-js',
        data: { error: error.message },
      })
      return []
    }
  }

  async getAppointmentAttachments(): Promise<Record<string, unknown>[]> {
    const attachments = (await makePromise(this.item.getAttachmentsAsync, this.item)()) as {
      id: string
      name: string
      contentType?: string
    }[]

    const attachmentsWithContent = await Promise.all(
      attachments.map(async ({ id, name, contentType }) => {
        const { content } = await makePromise(this.item.getAttachmentContentAsync, this.item)(id)

        return {
          title: name,
          mime_type: contentType || 'foo/bar', // temporary solution, because mime_tyle is required for gmail, but not for outlook
          file_url: 'https://microsoft.com',
          content: content,
        }
      })
    )

    return attachmentsWithContent
  }

  async getInterlocutorDetails(): Promise<InterlocutorDetails> {
    if (!this.hasItem()) {
      return { emailAddress: null, displayName: null }
    }
    const itemFrom = this.getFrom()
    const [firstInterlocutor] = await this.getTo()

    if (itemFrom.emailAddress !== this.office.context.mailbox?.userProfile.emailAddress) {
      return {
        emailAddress: itemFrom.emailAddress,
        displayName: itemFrom.displayName ?? null,
      }
    } else if (firstInterlocutor) {
      return {
        emailAddress: firstInterlocutor.emailAddress,
        displayName: firstInterlocutor.displayName ?? null,
      }
    }

    return { emailAddress: null, displayName: null }
  }

  setSubject(subject: string | undefined): Promise<unknown> {
    return makePromise(this.item.subject.setAsync, this.item.subject)(subject || '')
  }

  setLocation(location: string | null | undefined): Promise<unknown> {
    return makePromise(this.item.location.setAsync, this.item.location)(location || '')
  }

  setSubjectInNewMessage(subject: string): void {
    this.getSubject().then(result => {
      if (result.indexOf('Re: ') !== 0) {
        this.setSubject(subject)
      }
    })
  }

  setSubjectUnlessPresent(subject: string): void {
    const { hostVersion } = this.office.context.mailbox?.diagnostics
    const versionWithOldUI = '16'

    if (!this.item.conversationId) {
      this.setSubject(subject)
      return
    } else if (isDesktopMac() && hostVersion > versionWithOldUI) {
      this.setSubjectInNewMessage(subject)
    }
  }

  async getTemplateId(): Promise<number | null> {
    return this.getCustomPropertyValue('templateId', null) as Promise<number | null>
  }

  async getCrmReferences(): Promise<Record<string, unknown> | null> {
    return this.getCustomPropertyValue('crmReferences', null) as Promise<Record<
      string,
      unknown
    > | null>
  }

  async getMeetingTypeId(): Promise<number | string | null> {
    return this.getCustomPropertyValue('meetingTypeId', null) as Promise<number | string | null>
  }

  async getCrmCustomFields(): Promise<Record<string, unknown>> {
    return this.getCustomPropertyValue('crmCustomFields', {}) as Promise<Record<string, unknown>>
  }

  async getIsLofting(): Promise<boolean> {
    return this.getCustomPropertyValue('isLoft', false) as Promise<boolean>
  }

  async getMainGuest(): Promise<Record<string, unknown>> {
    return this.getCustomPropertyValue('mainGuest', {}) as Promise<Record<string, unknown>>
  }

  async getMessageIds(): Promise<{ ewsItemId: string; restItemId: string }> {
    const itemId = (await this.save()) as string

    const restVersion = this.office.MailboxEnums.RestVersion.v2_0
    const ewsItemId = this.office.context.mailbox?.convertToEwsId(itemId, restVersion)
    const restItemId = this.office.context.mailbox?.convertToRestId(itemId, restVersion)
    return { ewsItemId, restItemId }
  }

  async getMessage(): Promise<{
    ewsItemId: string
    to: string
    subject: string
    body: string
    attachments: Record<string, unknown>[]
    templateId: number | null
  }> {
    addBreadcrumb({ message: 'Item: getMessage', category: 'office-js' })
    const { ewsItemId, restItemId } = await this.getMessageIds()
    this.restMessage = (await this.rest.getCurrentMessage(restItemId)) as RestMessageCache

    const [[{ emailAddress }], subject, body, attachments, templateId] = await Promise.all([
      this.getTo(),
      this.getSubject(),
      this.getBody(),
      this.getAttachments(),
      this.getTemplateId(),
    ])

    return {
      ewsItemId,
      to: emailAddress,
      subject,
      body,
      attachments,
      templateId,
    }
  }
}
